import { defineDocument, type LineGridDef } from './types.js'
import {
  ALL_TABS, BASIC_ACTIONS, FULL_ACTIONS, REVISABLE_TABS, SENDABLE_ACTIONS,
  SYNCED_ACTIONS, SYNCED_BASIC_ACTIONS,
  adjustmentsGrid, baseColumns, baseFilters, chargesGrid, col, f,
  currencySection, fcyColumns, interstate, jobType, pricedLines, taxUnit, termsSection,
} from './common.js'

const YES_NO = [{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }] as const

/**
 * docs/06 §1 — the suppliers invited to price an RFQ.
 *
 * A participant is a line, so inviting one, recording what they said and
 * awarding the work all ride on the chassis every other grid uses. The
 * invitation token the supplier portal reads hangs off this row.
 */
const participantGrid: LineGridDef = {
  section: 'PARTICIPANT',
  title: 'Suppliers invited', n: '04',
  addLabel: 'Add a supplier',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'supplierId', label: 'Supplier', type: 'text', editable: true, lookup: { entity: 'supplier' } },
    { key: 'contactEmail', label: 'Contact email', type: 'text', width: 220, editable: true,
      hint: 'Where the invitation goes. Left empty the supplier record is used.' },
    { key: 'inviteState', label: 'Invitation', type: 'text', width: 150 },
    { key: 'responseState', label: 'Response', type: 'text', width: 160 },
    { key: 'quotedTotal', label: 'Quoted', type: 'money', width: 150, align: 'right' },
    { key: 'decision', label: 'Decision', type: 'text', width: 150, editable: true,
      options: [
        { value: '', label: 'Not decided' },
        { value: 'AWARDED', label: 'Awarded' },
        { value: 'REJECTED', label: 'Rejected' },
      ] },
  ],
  sources: [],
}

/** How a requisitioned material is to be bought (docs/24 §3.4). */
export const PROCUREMENT_ROUTES = [
  ['RFQ', 'Ask for quotations'],
  ['DIRECT', 'Order directly'],
  ['RATE_CONTRACT', 'Against a rate contract'],
  ['LOCAL', 'Buy locally'],
] as const satisfies ReadonlyArray<[string, string]>

/**
 * docs/24 §3.4 — the purchase executive's decision about what the site asked for.
 *
 * "The material request will convert into purchase requisition by the
 * purchase executive." The request says what the site wants; the requisition
 * says what purchasing will actually buy, how, and by when — having looked at
 * the stock on the shelf, the class of each material and how long it takes to
 * arrive. Several sites' requests can be bought together, which is why a
 * requisition may span projects: each line says which job it is for.
 *
 * It consumes the request lines it takes up, and is consumed in turn by the
 * orders raised from it. The enquiry it goes out on in between spends nothing.
 */
export const purchaseRequisition = defineDocument({
  key: 'purchase_requisition',
  group: 'Purchase and Expense',
  blurb: 'Turn approved material requests into what purchasing will buy — how much, how, and by when.',
  route: 'pur_reqs',
  label: 'Purchase requisition',
  labelPlural: 'Purchase requisitions',
  numbering: 'PURCHASE_REQUISITION',
  numberPrefix: 'PR/',
  workflow: 'PURCHASE_REQUISITION',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a purchase requisition',
    subtitle: 'For one project, or several sites’ requests bought together.',
    fields: [
      f.radio('requisitionFor', 'What is this for?', [
        ['PROJECT', 'One project'],
        ['CONSOLIDATED', 'Several projects bought together'],
      ]),
      { ...f.look('projectId', 'Project', 'project', true, 'full'),
        visibleWhen: { field: 'requisitionFor', equals: ['PROJECT'] } },
      f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('requisitionFor', 'For', [['PROJECT', 'One project'], ['CONSOLIDATED', 'Several projects']], false),
        { ...f.look('projectId', 'Project', 'project'),
          visibleWhen: { field: 'requisitionFor', equals: ['PROJECT'] } },
        { ...f.look('materialRequestId', 'Against material request', 'material_request'),
          hint: 'Name one request to pull its open lines. Or pick from every open request below.' },
        f.date('documentDate', 'Requisition date', true),
        { ...f.date('requiredDate', 'Needed on site by', true),
          hint: 'The default for every line. A line can say otherwise.' },
        f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true),
        f.choice('priority', 'Priority', [['NORMAL', 'Normal'], ['URGENT', 'Urgent']], false),
        { ...f.choice('procurementRoute', 'Usually bought through', PROCUREMENT_ROUTES, false),
          hint: 'The default for every line. Class C materials are usually bought locally.' },
        { ...f.look('warehouseId', 'Deliver to warehouse', 'warehouse'),
          hint: 'Left empty the materials go to the project site.' },
        jobType(),
        taxUnit(false),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials to buy', n: '02',
    columns: [
      col.seq(), col.item(), col.desc(),
      // Worked out on save from the item, its category and the settings — the
      // buyer reads them, and does not type them.
      { key: 'abcClass', label: 'Class', type: 'text', width: 76 },
      { key: 'sourceQty', label: 'Requested', type: 'qty', width: 110, align: 'right' },
      { key: 'stockOnHand', label: 'In stock', type: 'qty', width: 104, align: 'right' },
      col.qty('To buy'),
      col.unit(),
      { key: 'requiredBy', label: 'Needed by', type: 'date', width: 136, editable: true,
        hint: 'Left empty, the date on the header.' },
      { key: 'leadTimeDays', label: 'Lead time', type: 'text', width: 100, align: 'right' },
      { key: 'orderBy', label: 'Order by', type: 'date', width: 128 },
      { key: 'route', label: 'Buy through', type: 'text', width: 170, editable: true,
        options: PROCUREMENT_ROUTES.map(([value, label]) => ({ value, label })) },
      col.rate('Estimated rate'),
      col.amount('Estimated value'),
      { key: 'forProjectId', label: 'For', type: 'text', width: 180, editable: true, lookup: { entity: 'project' },
        hint: 'Which job this is bought for, on a requisition that covers several.' },
      col.boqRef(),
    ],
    sources: [
      { key: 'request', label: 'Pick from material requests', source: 'request' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  columns: [
    ...baseColumns(null, 'Estimated value'),
    { key: 'fulfilmentStatus', label: 'Progress', type: 'text', width: 210 },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'purchaseExecutiveId', label: 'Purchase executive', control: 'lookup', lookup: { entity: 'employee' } },
  ],
  actions: [...BASIC_ACTIONS, 'clone', 'preClose', 'undoPreClose'],
  // The purchase plan gates buying from a request, whether the order is raised
  // from the request or through a requisition.
  rules: ['excess_purchase_quantity', 'purchase_plan_required'],
  allocatesFrom: ['material_request'],
  createsNext: [
    { docType: 'request_for_quote', label: 'Request for quote', whenStatus: 'APPROVED' },
    { docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' },
  ],
} as const)

/**
 * docs/24 §3.6 — how a supplier has done, weighed criterion by criterion.
 *
 * Each row is one criterion from the master with its weight. The system
 * fills in what it can measure — on-time deliveries, what passed inspection,
 * quotations won, paperwork in order, the credit given, orders completed —
 * and suggests a score out of five from it; the evaluator scores the rest and
 * may overrule a suggestion. The weighted total out of 100 grades the
 * supplier, and the latest approved evaluation is the one the comparative
 * statement weighs against price.
 */
export const vendorEvaluation = defineDocument({
  key: 'vendor_evaluation',
  group: 'Purchase and Expense',
  blurb: 'Score a supplier or subcontractor on quality, price, delivery, compliance and the rest.',
  route: 'vendor_evals',
  label: 'Vendor evaluation',
  labelPlural: 'Vendor evaluations',
  numbering: 'VENDOR_EVALUATION',
  numberPrefix: 'VE/',
  workflow: 'VENDOR_EVALUATION',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Evaluate a vendor',
    subtitle: 'Choose who, and the period their orders and deliveries are measured over.',
    fields: [
      f.radio('vendorKind', 'Who is being evaluated?', [['SUPPLIER', 'A supplier'], ['SUBCONTRACTOR', 'A subcontractor']]),
      { ...f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
        visibleWhen: { field: 'vendorKind', equals: ['SUPPLIER'] } },
      { ...f.look('supplierId', 'Subcontractor', 'subcontractor', true, 'full'),
        visibleWhen: { field: 'vendorKind', equals: ['SUBCONTRACTOR'] } },
      f.date('periodFrom', 'Measured from', true),
      f.date('periodTo', 'Measured to', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('vendorKind', 'Vendor', [['SUPPLIER', 'Supplier'], ['SUBCONTRACTOR', 'Subcontractor']], false),
        f.look('supplierId', 'Vendor', 'party', true),
        f.date('documentDate', 'Evaluated on', true),
        f.date('periodFrom', 'Measured from', true),
        f.date('periodTo', 'Measured to', true),
        f.look('evaluatedById', 'Evaluated by', 'employee'),
        { key: 'weightedScore', label: 'Score (out of 100)', control: 'text', width: 'half',
          hint: 'Worked out from the criteria below when the evaluation is saved.' },
        { key: 'grade', label: 'Grade', control: 'text', width: 'half',
          hint: 'A 80 and over — preferred; B 60 — approved; C 40 — conditional; D — not recommended.' },
        f.notes('strengths', 'Strengths'),
        f.notes('improvements', 'What must improve'),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Criteria', n: '02',
    addLabel: 'Add a criterion',
    totalsAs: 'NONE',
    columns: [
      col.seq(),
      { key: 'criterionId', label: 'Criterion', type: 'text', width: 260, editable: true,
        lookup: { entity: 'vendor_criterion', fills: { weight: 'weight', measure: 'measure' } } },
      { key: 'weight', label: 'Weight %', type: 'rate', width: 110, align: 'right', editable: true },
      { key: 'measured', label: 'What the records show', type: 'text', width: 300 },
      { key: 'suggestedScore', label: 'Suggested', type: 'text', width: 100, align: 'right' },
      { key: 'score', label: 'Score', type: 'text', width: 150, editable: true,
        options: [
          { value: '5', label: '5 — Excellent' },
          { value: '4', label: '4 — Very good' },
          { value: '3', label: '3 — Good' },
          { value: '2', label: '2 — Fair' },
          { value: '1', label: '1 — Poor' },
        ],
        hint: 'Left empty, the suggestion is used where there is one.' },
      { key: 'points', label: 'Points', type: 'rate', width: 100, align: 'right' },
      { key: 'remarks', label: 'Remarks', type: 'text', width: 220, editable: true },
    ],
    sources: [{ key: 'criteria', label: 'Load the criteria', source: 'criteria' }],
  },
  // A score, not a value, and no project: an evaluation is of the vendor.
  columns: [
    ...baseColumns('supplier', 'Vendor').filter((c) => c.key !== 'total' && c.key !== 'projectName'),
    { key: 'weightedScore', label: 'Score', type: 'text' as const, width: 110, align: 'right' as const },
    { key: 'grade', label: 'Grade', type: 'text' as const, width: 100 },
  ],
  filters: baseFilters('supplier', 'Vendor').filter((f) => f.key !== 'projectId'),
  actions: BASIC_ACTIONS,
  rules: ['evaluation_weights'],
} as const)

/**
 * docs/24 §3.7 — the supplier telling us what is on its way.
 *
 * "Supplier processing and dispatch": between the order and the receipt the
 * buyer's only question is where the goods are, and the answer was in a phone
 * call. A dispatch advice records it against the order — what was sent, when,
 * with which transporter under which LR number and e-way bill, and the
 * invoice that travels with it — so expediting is a list rather than a round
 * of calls, and the receipt note at the gate starts from the paperwork the
 * supplier already sent.
 *
 * It spends nothing on the order: the receipt does that. A dispatch that never
 * arrives leaves the order exactly as open as it was.
 */
export const supplierDispatch = defineDocument({
  key: 'supplier_dispatch',
  group: 'Purchase and Expense',
  blurb: 'Record what a supplier has made ready or sent against an order: transporter, LR no, e-way bill, invoice.',
  route: 'sup_dispatches',
  label: 'Supplier dispatch',
  labelPlural: 'Supplier dispatches',
  numbering: 'SUPPLIER_DISPATCH',
  numberPrefix: 'DSP/',
  workflow: 'SUPPLIER_DISPATCH',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record a supplier dispatch',
    subtitle: 'Choose the order the goods are being sent against. Its open lines can be pulled in.',
    fields: [
      f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
      f.look('purchaseOrderId', 'Purchase order', 'purchase_order', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'Dispatch', fields: [
        { ...f.look('supplierId', 'Supplier', 'supplier', true), lockAfterDraft: true },
        { ...f.look('purchaseOrderId', 'Against order', 'purchase_order', true), lockAfterDraft: true },
        f.date('documentDate', 'Advised on', true),
        f.choice('dispatchStatus', 'Where it is', [
          ['IN_PRODUCTION', 'In production'],
          ['READY', 'Ready to dispatch'],
          ['DISPATCHED', 'Dispatched — in transit'],
          ['DELIVERED', 'Delivered'],
        ]),
        { ...f.date('expectedDispatchDate', 'Expected to dispatch on'),
          visibleWhen: { field: 'dispatchStatus', equals: ['IN_PRODUCTION', 'READY'] } },
        { ...f.date('dispatchedOn', 'Dispatched on'),
          visibleWhen: { field: 'dispatchStatus', equals: ['DISPATCHED', 'DELIVERED'] } },
        f.date('expectedArrival', 'Expected on site'),
        f.text('transporter', 'Transporter'),
        f.text('lrNo', 'LR / AWB no'),
        f.text('vehicleNo', 'Vehicle no'),
        f.text('driverContact', 'Driver contact'),
        f.text('ewayBillNo', 'e-Way bill no'),
        f.text('supplierInvoiceNo', "Supplier's invoice no"),
        f.date('supplierInvoiceDate', "Supplier's invoice date"),
        f.yesNo('testCertificateSent', 'Test certificate / TDS sent with the goods'),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials dispatched', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      col.ordered(), col.open(),
      col.qty('Dispatched'), col.unit(false), col.rate('Rate', false), col.amount(),
    ],
    sources: [{ key: 'order', label: 'Pull the open order lines', source: 'order' }],
  },
  columns: [
    ...baseColumns('supplier', 'Supplier', 'Value'),
    { key: 'dispatchStatus', label: 'Where it is', type: 'text', width: 170 },
    { key: 'expectedArrival', label: 'Expected', type: 'date', width: 128 },
  ],
  filters: baseFilters('supplier', 'Supplier'),
  actions: BASIC_ACTIONS,
  rules: [],
  // A link to the order line, for "pull the open lines" and for expediting;
  // `v_line_link.consumes` is false for it, so the order stays open.
  allocatesFrom: ['purchase_order'],
  createsNext: [{ docType: 'goods_receipt', label: 'Receipt note (GRN)', whenStatus: 'APPROVED' }],
} as const)

/** docs/06 §1 — ask several suppliers to price the same list. */
export const requestForQuote = defineDocument({
  key: 'request_for_quote',
  group: 'Purchase and Expense',
  blurb: 'Create an RFQ for materials. You can add estimated or requested materials from the project.',
  route: 'rfq',
  label: 'Request for quote',
  labelPlural: 'Requests for quote',
  numbering: 'RFQ',
  numberPrefix: 'RFQ-{CO} ',
  workflow: 'RFQ',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Request for quote',
    subtitle: 'An RFQ can be for a confirmed project, for an opportunity still under quotation, or a periodic price check.',
    fields: [
      f.radio('rfqFor', 'What is this RFQ for?', [['PROJECT', 'A project'], ['GENERAL', 'A general price check']]),
      { ...f.look('projectId', 'Project', 'project', true, 'full'),
        visibleWhen: { field: 'rfqFor', equals: ['PROJECT'] } },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.date('documentDate', 'Document date', true),
        f.date('responseDueDate', 'Responses due by', true),
        // "Close the RFQ after the process is over" — after which the supplier
        // portal stops accepting, whatever the invitation link says
        // (docs/video-notes/06 §5).
        { ...f.choice('rfqStatus', 'Is this still open?', [['OPEN', 'Open'], ['CLOSED', 'Closed']], false),
          hint: 'Closing it stops suppliers responding, without cancelling the request.' },
        f.text('deliveryTimeline', 'Delivery timeline', 'half', true),
        f.look('projectId', 'Project', 'project'),
        f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true),
        jobType(),
        taxUnit(false),
        f.notes('notesToSupplier', 'Notes to the supplier'),
        f.notes(),
      ] },
      { key: 'address', n: '02', title: 'Address and correspondence', fields: [
        f.look('billingAddressId', 'Billing address', 'address', true),
        f.look('deliveryAddressId', 'Delivery address', 'address', true),
        f.look('contactId', 'Company contact', 'contact', true),
      ] },
    ],
  },
  lines: {
    ...pricedLines('Materials', '03'),
    // The vendor's three sources: what was estimated, what the site actually
    // asked for, and anything at all (docs/video-notes/06 §5).
    sources: [
      { key: 'estimation', label: 'Pick from the estimate', source: 'estimation' },
      { key: 'request', label: 'Pick from material requests', source: 'request' },
      { key: 'requisition', label: 'Pick from purchase requisitions', source: 'requisition' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  grids: [participantGrid, chargesGrid('05')],
  columns: baseColumns(null, 'Estimated value'),
  filters: baseFilters(null),
  actions: SENDABLE_ACTIONS,
  rules: [],
  requiresSetting: 'rfq_enabled',
  createsNext: [{ docType: 'supplier_quotation', label: 'Supplier quotation', whenStatus: 'APPROVED' }],
} as const)

/** docs/06 §2 — one supplier's answer to an RFQ. */
export const supplierQuotation = defineDocument({
  key: 'supplier_quotation',
  group: 'Purchase and Expense',
  blurb: 'Record a supplier response to an RFQ. The online response form creates these automatically.',
  route: 'sup_quotes',
  label: 'Supplier quotation',
  labelPlural: 'Supplier quotations',
  numbering: 'SUPPLIER_QUOTATION',
  numberPrefix: 'Quote-',
  workflow: 'SUPPLIER_QUOTATION',
  tabs: REVISABLE_TABS,
  createWizard: {
    title: 'Supplier quotation',
    subtitle: 'Choose the RFQ and the supplier. Only one quotation is allowed per participant.',
    fields: [
      f.look('requestForQuoteId', 'Request for quote', 'request_for_quote', true, 'full'),
      f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('requestForQuoteId', 'Request for quote', 'request_for_quote', true),
        f.look('supplierId', 'Supplier', 'supplier', true),
        f.date('documentDate', 'Document date', true),
        f.text('supplierQuoteNo', "Supplier's quote no"),
        f.date('validUpto', 'Valid up to'),
        f.look('purchaseExecutiveId', 'Purchase executive', 'employee', true),
        f.text('deliveryLeadTime', 'Delivery lead time'),
        f.text('paymentTerms', 'Payment terms'),
        // docs/24 §3.5 — what this supplier offered, which the comparison weighs;
        // left empty the supplier record's usual credit is assumed.
        { key: 'creditPeriodDays', label: 'Credit offered (days)', control: 'number', width: 'half',
          hint: 'Left empty, the credit period on the supplier record.' },
        f.choice('decision', 'Decision', [['', 'Not decided'], ['AWARDED', 'Awarded'], ['REJECTED', 'Rejected']], false),
        f.notes('supplierNotes', 'Notes from the supplier'),
        f.notes(),
      ] },
    ],
  },
  lines: pricedLines('Quoted materials', '02'),
  grids: [chargesGrid('03')],
  columns: [
    ...baseColumns('supplier', 'Supplier', 'Quoted value'),
    { key: 'createdFromPortal', label: 'From the portal', type: 'text', width: 150 },
  ],
  filters: baseFilters('supplier', 'Supplier'),
  actions: FULL_ACTIONS,
  rules: ['one_quotation_per_participant', 'quotation_from_participant'],
  requiresSetting: 'rfq_enabled',
  // Each quoted line points at the RFQ line it prices — "Pull the open lines"
  // reads the named RFQ — which is what the comparison compares (docs/24 §3.5).
  // The link spends nothing: every supplier prices the same line.
  allocatesFrom: ['request_for_quote'],
  createsNext: [{ docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' }],
} as const)

/** docs/06 §3 — agreed prices, valid for a period. */
export const supplierRateContract = defineDocument({
  key: 'supplier_rate_contract',
  group: 'Purchase and Expense',
  blurb: 'Agree rates for materials with a supplier, with validity dates.',
  route: 'sup_rate_contracts',
  label: 'Supplier rate contract',
  labelPlural: 'Supplier rate contracts',
  numbering: 'SUPPLIER_RATE_CONTRACT',
  numberPrefix: 'SRC-',
  workflow: 'SUPPLIER_RATE_CONTRACT',
  tabs: REVISABLE_TABS,
  createWizard: {
    title: 'Supplier rate contract',
    fields: [
      f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
      f.date('validTill', 'Valid till', true, 'full'),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('supplierId', 'Supplier', 'supplier', true),
        f.date('documentDate', 'Document date', true),
        f.date('validTill', 'Valid till', true),
        f.text('supplierDocNo', "Supplier's document no"),
        f.look('contactId', 'Supplier contact', 'contact'),
        f.look('supplierAddressId', 'Supplier address', 'address'),
        taxUnit(),
        f.text('paymentTerms', 'Delivery and payment terms', 'full'),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Agreed rates', n: '02',
    columns: [col.seq(), col.item(), col.desc(), col.brand(), col.unit(), col.rate('Agreed rate')],
    sources: [{ key: 'excel', label: 'Import a price list', source: 'excel' }],
  },
  columns: [...baseColumns('supplier', 'Supplier', 'Contract value'), ...fcyColumns('Contract value')],
  filters: baseFilters('supplier', 'Supplier'),
  actions: FULL_ACTIONS,
  rules: [],
} as const)

/** docs/06 §5 — labour and installation, priced against billing terms. */
export const subcontractOrder = defineDocument({
  key: 'subcontract_order',
  group: 'Purchase and Expense',
  blurb: 'Create new orders for estimated subcontract items. You can also view existing orders.',
  route: 'sub_ords',
  label: 'Subcontract order',
  labelPlural: 'Subcontract orders',
  numbering: 'SUBCONTRACT_ORDER',
  numberPrefix: 'WO/{CO}',
  workflow: 'SUBCONTRACT_ORDER',
  tabs: REVISABLE_TABS,
  createWizard: {
    title: 'Create a subcontract order',
    subtitle: 'You can also copy another order once this is open.',
    fields: [
      f.look('projectId', 'Project', 'project', true),
      f.look('supplierId', 'Subcontractor', 'subcontractor', true),
      f.radio('billingTermsApplicable', 'Billing terms applicable?', [['YES', 'Yes'], ['NO', 'No']]),
      f.choice('scope', 'Scope', [['INSTALLATION', 'Installation'], ['SUPPLY_AND_INSTALLATION', 'Supply and installation']]),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.look('supplierId', 'Subcontractor', 'subcontractor', true),
        f.date('documentDate', 'Document date', true),
        f.choice('scope', 'Scope', [['INSTALLATION', 'Installation'], ['SUPPLY_AND_INSTALLATION', 'Supply and installation']]),
        f.choice('billingTermsApplicable', 'Billing terms applicable?', [['YES', 'Yes'], ['NO', 'No']], false),
        f.look('supplierBillingTermId', 'Supplier billing term', 'supplier_billing_term'),
        jobType(),
        taxUnit(),
        f.look('purchaseExecutiveId', 'Purchase executive', 'employee'),
        f.date('dueDate', 'Due by'),
        interstate(),
        f.notes('notesToSupplier', 'Notes to the subcontractor'),
        f.notes(),
      ] },
      { key: 'delivery', n: '02', title: 'Site information', fields: [
        f.look('deliveryAddressId', 'Work site', 'address', true),
        f.look('contactId', 'Site contact', 'contact'),
      ] },
      currencySection(),
      termsSection(),
    ],
  },
  lines: pricedLines('Scope of work', '03', 'Work item'),
  grids: [chargesGrid(), adjustmentsGrid()],
  columns: [
    ...baseColumns('subcontractor', 'Subcontractor'),
    { key: 'fulfilmentStatus', label: 'Progress', type: 'text', width: 210 },
  ],
  filters: baseFilters('subcontractor', 'Subcontractor'),
  actions: [...SYNCED_ACTIONS, 'preClose', 'undoPreClose'],
  rules: ['rate_exceeds_estimation', 'excess_purchase_quantity', 'supplier_not_approved'],
  createsNext: [
    { docType: 'work_progress', label: 'Work progress', whenStatus: 'APPROVED' },
    { docType: 'purchase_invoice', label: 'Purchase invoice', whenStatus: 'APPROVED' },
  ],
} as const)

/** docs/17 §2 — measured progress against a subcontract, fully priced and taxed. */
/**
 * The JMR — the joint measurement record a claimed quantity comes from.
 *
 * "Create the work progress of the installation based on JMR (Measurement
 * Reading)" is the vendor's own wording, and it is the step that makes a
 * subcontractor's claim checkable. Without it the quantity on the progress
 * line is an assertion, and the engineer who thinks it is wrong has nothing to
 * argue with (docs/video-notes/06 §3).
 *
 * Each row measures one location: how many of a thing, and its dimensions. The
 * total is checked against the quantity claimed on the work item it hangs off.
 */
const measurementGrid: LineGridDef = {
  section: 'MEASUREMENT',
  parentSection: 'ITEM',
  title: 'Measurement sheet (JMR)', n: '03',
  addLabel: 'Add a measurement',
  totalsAs: 'NONE',
  columns: [
    col.seq(),
    { key: 'parentLineId', label: 'For which work', type: 'text', width: 240, editable: true,
      parentPicker: true,
      hint: 'The line above that this measurement counts towards.' },
    { key: 'description', label: 'Location', type: 'text', editable: true,
      hint: 'Where on site this was measured — grid line, floor, room.' },
    { key: 'nos', label: 'Nos', type: 'qty', width: 88, align: 'right', editable: true },
    { key: 'lengthM', label: 'Length', type: 'qty', width: 96, align: 'right', editable: true },
    { key: 'breadthM', label: 'Breadth', type: 'qty', width: 100, align: 'right', editable: true },
    { key: 'depthM', label: 'Depth', type: 'qty', width: 92, align: 'right', editable: true },
    // Nos x Length x Breadth x Depth, worked out by the server. Typed by hand
    // it was a number beside four others that did not have to agree with it,
    // and the mismatch rule was checking one assertion against another.
    col.qty('Measured', false),
    col.unit(),
  ],
  sources: [],
}

export const workProgress = defineDocument({
  key: 'work_progress',
  group: 'Purchase and Expense',
  blurb: "Record progress of subcontracts. Review the supplier's invoice against the recorded progress.",
  route: 'work_progress',
  label: 'Work progress',
  labelPlural: 'Work progress',
  numbering: 'WORK_PROGRESS',
  numberPrefix: 'WP/',
  workflow: 'WORK_PROGRESS',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record work progress',
    subtitle: 'Progress against a subcontractor’s order, or against work your own people did.',
    fields: [
      /*
        Three cases, not two. The third is the common one on a live site and
        used to have nowhere to go: several contractors working the same job
        with no order issued to any of them, their quantities recorded daily by
        our own engineer against the BOQ, and none of it ever becoming a
        subcontractor bill (docs/video-notes/06 §3).
      */
      f.radio('progressFor', 'Whose work is this?', [
        ['SUBCONTRACTOR', "A subcontractor's, against their order"],
        ['CONTRACTOR_NO_ORDER', 'A contractor with no order — measured by us'],
        ['OWN_LABOUR', 'Our own labour'],
      ]),
      { ...f.look('supplierId', 'Subcontractor', 'subcontractor', true),
        visibleWhen: { field: 'progressFor', equals: ['SUBCONTRACTOR'] } },
      { ...f.look('subcontractOrderId', 'Subcontract order', 'subcontract_order', true),
        visibleWhen: { field: 'progressFor', equals: ['SUBCONTRACTOR'] } },
      { ...f.look('supplierId', 'Contractor', 'subcontractor', true),
        hint: 'Who did the work. No order is raised and no invoice follows from this.',
        visibleWhen: { field: 'progressFor', equals: ['CONTRACTOR_NO_ORDER'] } },
      { ...f.look('projectId', 'Project', 'project', true),
        visibleWhen: { field: 'progressFor', equals: ['CONTRACTOR_NO_ORDER', 'OWN_LABOUR'] } },
      f.date('documentDate', 'As on date', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('progressFor', 'Whose work is this?', [
          ['SUBCONTRACTOR', 'A subcontractor'],
          ['CONTRACTOR_NO_ORDER', 'A contractor with no order'],
          ['OWN_LABOUR', 'Our own labour'],
        ], false),
        f.look('subcontractOrderId', 'Subcontract order', 'subcontract_order'),
        f.look('supplierId', 'Contractor', 'subcontractor'),
        f.date('documentDate', 'As on date', true),
        f.date('periodFrom', 'Period from'),
        f.choice('progressMethod', 'Progress measured as', [['CUMULATIVE', 'Cumulative — total done to date'], ['MINIMUM', 'This period only']]),
        f.look('projectId', 'Project', 'project', true),
        /*
          How many people were on site for this.

          "We are not giving it like a lump sum. We have to calculate how many
          people are coming in daily — we need those reports." Recorded here
          rather than as its own document because it is the same entry, made by
          the same person, about the same day's work
          (docs/video-notes/06 §5).
        */
        { key: 'labourCount', label: 'People on site', control: 'number', width: 'half',
          hint: 'How many worked on this today. Left empty, nothing is counted for the day.' },
        { key: 'labourHours', label: 'Hours worked', control: 'number', width: 'half',
          hint: 'Total man-hours, where the contract is measured in them.' },
        jobType(),
        taxUnit(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Work completed', n: '02',
    columns: [
      col.seq(), col.item('Work item'), col.desc(), col.boqRef(),
      { key: 'supplierBillingTermId', label: 'Billing term', type: 'text', width: 170, editable: true,
        lookup: { entity: 'supplier_billing_term' } },
      col.qty('Completed'), col.unit(), col.rate(), col.hsn(), col.tax(), col.amount(),
    ],
    /*
      Two ways in, because there are two ways work happens.

      Against an order, the order's own lines are pulled. Without one — our own
      labour, or a contractor nobody issued an order to — the estimate is the
      only thing that knows what the BOQ promised, and picking from it is what
      links the progress back to the BOQ line. Typed by hand the entry has no
      link at all: it would show no BOQ reference in the contractor report, and
      it would never move the Calculated quantity on a billing request, which
      is measured from exactly these links (docs/video-notes/06 §3, §7).
    */
    sources: [
      { key: 'order', label: 'Pull the order lines', source: 'order' },
      { key: 'estimation', label: 'Pick from the estimated work', source: 'estimation' },
    ],
  },
  grids: [measurementGrid, chargesGrid()],
  columns: baseColumns('subcontractor', 'Subcontractor', 'Progress value'),
  filters: baseFilters('subcontractor', 'Subcontractor'),
  actions: BASIC_ACTIONS,
  rules: ['excess_work_progress_quantity', 'progress_measurement_mismatch'],
  allocatesFrom: ['subcontract_order'],
  createsNext: [{ docType: 'purchase_invoice', label: 'Purchase invoice', whenStatus: 'APPROVED' }],
} as const)

/** docs/17 §3 — the actual against an estimated overhead line. */
export const otherCost = defineDocument({
  key: 'other_cost',
  group: 'Purchase and Expense',
  blurb: 'Record project costs other than purchases and subcontracts.',
  route: 'other_costs',
  label: 'Other cost',
  labelPlural: 'Other costs',
  numbering: 'OTHER_COST',
  numberPrefix: 'OC/',
  workflow: 'OTHER_COST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record another cost',
    subtitle: 'For cost that is neither a purchase nor a subcontract — consumables, fittings, hire.',
    fields: [
      f.look('projectId', 'Project', 'project', true, 'full'),
      f.date('documentDate', 'Date', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.date('documentDate', 'Date', true),
        f.look('supplierId', 'Paid to', 'supplier'),
        jobType(),
        taxUnit(),
        interstate(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Costs', n: '02',
    columns: [
      col.seq(),
      { key: 'itemId', label: 'Cost head', type: 'text', editable: true, lookup: { entity: 'item' } },
      col.desc(), col.boqRef(), col.qty(), col.unit(), col.rate(), col.hsn(), col.tax(), col.amount('Amount'),
    ],
    sources: [{ key: 'estimation', label: 'Pick from the estimated overheads', source: 'estimation' }],
  },
  columns: baseColumns('supplier', 'Paid to', 'Cost'),
  filters: baseFilters('supplier', 'Paid to'),
  actions: SYNCED_BASIC_ACTIONS,
  rules: [],
} as const)

/** docs/17 §4 — staff expenses and advances: one document, two modes. */
export const expenseReport = defineDocument({
  key: 'expense_report',
  group: 'Purchase and Expense',
  blurb: 'Report your project and service expenses here, and request any advances you need.',
  route: 'expense_reports',
  label: 'Expense report',
  labelPlural: 'Expense reports',
  numbering: 'EXPENSE_REPORT',
  numberPrefix: 'EXP/',
  workflow: 'EXPENSE_REPORT',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create an expense report',
    subtitle: 'Claiming what you spent, or asking for money up front.',
    fields: [
      f.radio('expenseType', 'Which is this?', [
        ['EXPENSE_REPORT', 'An expense report — money already spent'],
        ['ADVANCE_PAYMENT', 'An advance — money needed up front'],
      ]),
      f.look('employeeId', 'Whose is it?', 'employee', true, 'full'),
      f.look('projectId', 'Project', 'project', false, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('expenseType', 'Which is this?', [['EXPENSE_REPORT', 'Expense report'], ['ADVANCE_PAYMENT', 'Advance payment']], false),
        f.look('employeeId', 'Employee', 'employee', true),
        f.look('projectId', 'Project', 'project'),
        f.date('documentDate', 'Claim date', true),
        f.date('periodFrom', 'Period from'),
        f.date('periodTo', 'Period to'),
        { ...f.money('advanceRequested', 'Advance requested'),
          visibleWhen: { field: 'expenseType', equals: ['ADVANCE_PAYMENT'] } },
        taxUnit(false),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Expenses', n: '02',
    columns: [
      col.seq(),
      { key: 'itemId', label: 'Expense head', type: 'text', editable: true, lookup: { entity: 'item' } },
      col.desc(),
      { key: 'incurredOn', label: 'Incurred on', type: 'date', width: 130, editable: true },
      col.amount(),
    ],
    sources: [{ key: 'excel', label: 'Import from Excel', source: 'excel' }],
  },
  grids: [{
    section: 'ADJUSTMENT',
    title: 'Advances settled against this claim', n: '03',
    addLabel: 'Settle an advance',
    totalsAs: 'SUBTRACT',
    columns: [
      col.seq(),
      /*
        The advance itself, not its number typed out again.

        A free-text reference could not stop the same advance being settled
        twice or being settled for more than it was worth, and the
        `employee_advances` report's balance had no link to reconcile against.
        `advance_over_settled` can now check both.
      */
      { key: 'againstDocumentId', label: 'Advance', type: 'text', width: 220, editable: true,
        lookup: { entity: 'expense_report' } },
      col.desc(),
      col.amount('Amount used'),
    ],
    sources: [],
  }],
  columns: [
    ...baseColumns(null, 'Claimed'),
    { key: 'expenseType', label: 'Kind', type: 'text', width: 160 },
  ],
  filters: baseFilters(null),
  actions: SYNCED_BASIC_ACTIONS,
  rules: ['advance_over_settled'],
} as const)
